import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, ArrowRightIcon, BarChart3Icon, TrashIcon, EyeIcon, MessageSquareIcon, HeartIcon, FlameIcon, Share2Icon, SparklesIcon, XIcon } from 'lucide-react';
import { formatDistanceToNow, isPast } from 'date-fns';
import type { Snap, SnapActivity } from '@/types/database.types';
import ShareModal from '@/components/ShareModal';
import { useState, useEffect } from 'react';
import CategoryTags from '@/components/CategoryTags';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

interface BaseSnapCardProps {
  snap: Snap & { activity: SnapActivity };
  onDelete: (id: string) => void;
  onLike: (id: string, event: React.MouseEvent) => void;
  onVote?: (snapId: string, optionId: string) => void;
  onRemoveVote?: (snapId: string) => void;
  wrapperClassName?: string;
  badges?: React.ReactNode;
  isVoting?: boolean;
}

const DEFAULT_AVATAR = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';

export default function BaseSnapCard({ 
  snap, 
  onDelete, 
  onLike, 
  onVote, 
  onRemoveVote,
  wrapperClassName, 
  badges, 
  isVoting = false 
}: BaseSnapCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isExpired = isPast(new Date(snap.ends_at));
  const [showShareModal, setShowShareModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(snap.creator?.avatar_url || DEFAULT_AVATAR);
  const [expandedOption, setExpandedOption] = useState<string | null>(null);
  const [prevVoteCounts, setPrevVoteCounts] = useState<Record<string, number>>({});

  // Store previous vote counts for animation
  useEffect(() => {
    const newCounts: Record<string, number> = {};
    snap.options?.forEach(option => {
      newCounts[option.id] = option.vote_count;
    });
    setPrevVoteCounts(newCounts);
  }, [snap.options]);

  const handleImageError = () => {
    setImageError(true);
    setAvatarUrl(DEFAULT_AVATAR);
  };

  // Reset image error state when snap or avatar URL changes
  if (snap.creator?.avatar_url && snap.creator.avatar_url !== avatarUrl && !imageError) {
    setAvatarUrl(snap.creator.avatar_url);
    setImageError(false);
  }

  const handleCardClick = () => {
    if (user) {
      // If user is logged in, navigate directly to the SNAP
      navigate(`/snap/${snap.id}`);
    } else {
      // If user is not logged in, store the SNAP ID in localStorage and redirect to login
      localStorage.setItem('redirectSnapId', snap.id);
      navigate('/login');
    }
  };

  const handleOptionClick = (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigating to snap details
    
    if (onVote && !isExpired && !isVoting) {
      // Don't allow voting on the same option again
      if (snap.userVote === optionId) {
        toast.error('You have already voted for this option');
        return;
      }
      
      onVote(snap.id, optionId);
    } else if (isExpired) {
      // If the snap is expired, just show a message
      toast.error('This SNAP has ended and voting is closed');
    } else if (isVoting) {
      // If already voting, show a message
      toast('Vote in progress, please wait...', {
        icon: '⏳'
      });
    }
  };

  const handleRevokeVote = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigating to snap details
    
    if (onRemoveVote && !isExpired && !isVoting && snap.userVote) {
      onRemoveVote(snap.id);
    }
  };

  const toggleExpandOption = (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigating to snap details
    setExpandedOption(expandedOption === optionId ? null : optionId);
  };

  return (
    <>
      <div
        className={`${wrapperClassName || "relative bg-gradient-to-br from-white/25 to-white/15 backdrop-blur-sm border border-white/30 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden h-full flex flex-col"}`}
        onClick={handleCardClick}
        style={{
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)'
        }}
      >
        <div className="p-4 sm:p-6 flex flex-col flex-grow">
          {/* Activity Tags and Type Badge */}
          <div className="flex flex-wrap gap-2 mb-3">
            {snap.activity && getActivityBadge(snap.activity)}
            {badges}
          </div>

          {/* Title and Actions */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {snap.title}
            </h2>
            <div className="flex items-center m-1">
              {!snap.is_private && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowShareModal(true);
                  }}
                  className="text-gray-400 hover:text-primary-600 transition-colors"
                  title="Share SNAP"
                >
                  <Share2Icon className="h-5 w-5" />
                </button>
              )}
              {snap.isOwner && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(snap.id);
                  }}
                  className="text-gray-400 hover:text-red-600 transition-colors ml-2"
                  title="Delete SNAP"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Category Tags */}
          <CategoryTags categories={snap.categories} className="mb-4" />

          {/* User Info and Time */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mb-4">
            <div className="flex items-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={snap.is_anonymous ? 'Anonymous' : (snap.creator?.username || 'Unknown')}
                  className="h-6 w-6 rounded-full object-cover ring-2 ring-white mr-2"
                  onError={handleImageError}
                />
              ) : (
                <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-white mr-2">
                  <AlertCircleIcon className="h-4 w-4 text-gray-500" />
                </div>
              )}
              {snap.is_anonymous ? 'Anonymous' : (snap.creator?.username || 'Unknown')}
            </div>
            <div className="flex items-center">
              <AlertCircleIcon className="h-4 w-4 mr-1" />
              {isExpired ? 'Closed' : formatDistanceToNow(new Date(snap.ends_at), { addSuffix: true })}
            </div>
          </div>

          {/* Options */}
          {snap.options && (
            <div className="space-y-2 mb-4 flex-grow">
              {snap.options.map((option) => {
                const percentage = snap.total_votes > 0
                  ? (option.vote_count / snap.total_votes) * 100
                  : 0;

                const isVoted = snap.userVote === option.id;
                const isExpanded = expandedOption === option.id;
                
                return (
                  <motion.div 
                    key={option.id}
                    className={`space-y-1 p-2 rounded-md transition-colors ${
                      isVoted 
                        ? 'bg-primary-50 border border-primary-200' 
                        : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                    }`}
                    onClick={(e) => toggleExpandOption(option.id, e)}
                    whileHover={{ scale: 1.01 }}
                    animate={{ 
                      backgroundColor: isVoted ? 'rgba(239, 246, 255, 0.8)' : 'rgba(255, 255, 255, 0.8)'
                    }}
                  >
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 font-medium">{option.title}</span>
                      <AnimatePresence mode="wait">
                        <motion.span 
                          key={`percent-${percentage.toFixed(1)}`}
                          initial={{ opacity: 0, x: -5 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 5 }}
                          transition={{ duration: 0.3 }}
                          className="text-gray-900 font-medium"
                        >
                          {percentage.toFixed(1)}%
                        </motion.span>
                      </AnimatePresence>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full ${isVoted ? 'bg-primary-500' : 'bg-primary-600'}`}
                        initial={{ width: `${prevVoteCounts[option.id] ? (prevVoteCounts[option.id] / (snap.total_votes || 1)) * 100 : 0}%` }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ 
                          duration: 0.5,
                          ease: "easeInOut"
                        }}
                        key={`${option.id}-${option.vote_count}-${snap.total_votes}`} // Key changes when vote counts change
                      />
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-2 space-y-2">
                        {option.description && (
                          <p className="text-xs text-gray-600">{option.description}</p>
                        )}
                        
                        {!isExpired && (
                          <div className="flex space-x-2">
                            {isVoted ? (
                              // Show revoke button if this is the option the user voted for
                              <button
                                onClick={handleRevokeVote}
                                className="flex-1 text-xs py-1 px-2 rounded bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                disabled={isVoting}
                                title="Revoke your vote"
                              >
                                <div className="flex items-center justify-center">
                                  <XIcon className="h-3 w-3 mr-1" />
                                  Revoke
                                </div>
                              </button>
                            ) : (
                              // Show vote button for options the user hasn't voted for
                              <button
                                onClick={(e) => handleOptionClick(option.id, e)}
                                className="flex-1 text-xs py-1 px-2 rounded bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                                disabled={isVoting}
                              >
                                {isVoting ? 'Voting...' : 'Vote'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-200/50">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center" title="Total Votes">
                <BarChart3Icon className="h-4 w-4 mr-1" />
                <AnimatePresence mode="wait">
                  <motion.span
                    key={snap.total_votes}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    {snap.total_votes}
                  </motion.span>
                </AnimatePresence>
              </div>
              <div className="flex items-center" title="Views">
                <EyeIcon className="h-4 w-4 mr-1" />
                <span>{snap.view_count}</span>
              </div>
              <div className="flex items-center" title="Comments">
                <MessageSquareIcon className="h-4 w-4 mr-1" />
                <span>{snap.commentCount}</span>
              </div>
              <button
                onClick={(e) => onLike(snap.id, e)}
                className={`flex items-center transition-colors ${
                  snap.userLiked ? 'text-red-500 hover:text-red-600' : 'text-gray-600 hover:text-red-500'
                }`}
                title={snap.userLiked ? 'Unlike' : 'Like'}
                aria-label={snap.userLiked ? 'Unlike' : 'Like'}
              >
                <HeartIcon className={`h-4 w-4 mr-1 ${snap.userLiked ? 'fill-current' : ''}`} />
                <span className="leading-none">{snap.likes_count}</span>
              </button>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
              }}
              className="inline-flex items-center px-2 py-1.5 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-50 hover:bg-primary-100"
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {!snap.is_private && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          snapId={snap.id}
          snapTitle={snap.title}
        />
      )}
    </>
  );
}

const getActivityBadge = (activity: SnapActivity) => {
  switch (activity) {
    case 'hot':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <div className="flex -space-x-1">
            <FlameIcon className="h-3.5 w-3.5" />
            <FlameIcon className="h-3.5 w-3.5" />
            <FlameIcon className="h-3.5 w-3.5" />
          </div>
          <span className="ml-1">Hot</span>
        </span>
      );
    case 'trending':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <FlameIcon className="h-3.5 w-3.5" />
          <span className="ml-1">Trending</span>
        </span>
      );
    case 'new':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <SparklesIcon className="h-3.5 w-3.5" />
          <span className="ml-1">New</span>
        </span>
      );
    default:
      return null;
  }
};